import React, {useCallback, useRef} from 'react';
import {Helmet} from 'react-helmet';
import {useSnackbar} from 'notistack';
import {Box, Container, Stack, styled, Typography, useMediaQuery, useTheme} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import Banner from '../../components/Banner.js';
import {imagekitStaticURL} from '../../util/util.js';
import {withTitle} from '../../title.js';
import withMainContainer from '../../components/WithMainContainer.js';
import CardContainer from '../../components/CardContainer.js';
import SCButton from '../../components/SCButton.js';
import {useOnce, useQueryParam, useScrollTo} from '../../util/react_hooks.js';
import {SEC} from '../../util.js';
import {Payform} from './TarotmailPayform.js';

const CheckLine = ({children, color='primary', IconComp=CheckIcon})=><Stack direction="row"
  alignItems="center" gap={1}>
  <IconComp color={color}/>
  {children}
</Stack>;

const ImageCard = props=>{
    let {image, text, ...rest} = props;
    let theme = useTheme();
    let md = useMediaQuery(theme.breakpoints.down('md'));
    return <Stack direction="column" gap={2} alignItems="center" width="200px" {...rest}>
      <Box component="img" maxWidth="150px" src={image} sx={{objectFit: 'contain'}}/>
      <Typography textAlign="center" variant="h6">
        {text}
      </Typography>
    </Stack>;
};

const SubTypeInnerCard = styled(({className, children})=><Stack
  className={className} direction="column" padding={1} gap={1}
  alignItems="center" justifyContent="start">
  {children}
</Stack>, {shouldForwardProp: p=>!p.startsWith('$')})`
    border-radius: 8px;
    border: 2px solid ${p=>p.theme.palette.common.white};
    height: 100%;
`;

const SubTypeCard = styled(({className, children, ...rest})=><Stack
  className={className} padding={1} {...rest}>
  <SubTypeInnerCard>
    {children}
  </SubTypeInnerCard>
</Stack>, {shouldForwardProp: p=>!p.startsWith('$')})`
    border-radius: 12px;
    background-color: ${p=>p.theme.palette.primary.light};
    box-shadow: 4px 4px 8px rgba(0, 0, 0, 0.2);
`;

const TarotmailLanding = ()=>{
    let theme = useTheme();
    let sm = useMediaQuery(theme.breakpoints.down('sm'));
    let md = useMediaQuery(theme.breakpoints.down('md'));
    let purchaseSection = useRef();
    let scrollToPurchaseSection = useScrollTo(purchaseSection);
    return <Stack direction="column">
      <Banner image={imagekitStaticURL`tarot-banner.png`}
        placeholderColor="#242328" backgroundPosition="left">
        <Typography variant="h3" textAlign="center">
          לשלוח מסרים בצורה אוטומטית ולקבל פניות מלקוחות שרוצים ממך קריאות מבלי להתאמץ
        </Typography>
        <Typography variant="h5" textAlign="center">
          "אפליקציית המסרים" - כלי שיווקי שעובד בשבילך 24/7
        </Typography>
      </Banner>
      <Container maxWidth="lg">
        <Stack direction="column" gap={3} marginTop={3}>
          <Stack gap={4} justifyContent="center"
            direction={sm ? 'column' : 'row'}
            alignItems={sm ? 'center' : null}>
            <Helmet>
              <script src="https://player.vimeo.com/api/player.js"></script>
            </Helmet>
            <iframe src="https://player.vimeo.com/video/1141768985?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
              width={md ? '285' : '224'}
              height={md ? '500' : '400'}
              frameborder="0"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
              referrerpolicy="strict-origin-when-cross-origin"
              title="אפליקציית המסרים"></iframe>
            <Stack direction="column" gap={3} justifyContent="center">
              <Typography textAlign={sm ? 'center' : null}>
                דמייני לעצמך כמה מדהים זה היה -<br/>
                אם הייתה לך דרך לתת לאנשים מסרים מהטארוט, כל יום -<br/>
                בלי לשבת כל פעם מחדש ולשלוח אותם בעצמך.<br/>
                פשוט לתת להם לינק אחד,<br/>
                והם נרשמים לקבל ממך מסר יומי למייל - אוטומטית.<br/>
              </Typography>
              <Typography textAlign={sm ? 'center' : null}>
                והמערכת?<br/>
                היא לא רק שולחת את המסרים בשמך,<br/>
                היא גם מצרפת הצעה עדינה לשירותים שלך - פגישה, פריסה, קורס או כל דבר שתרצי לקדם.
              </Typography>
              <Typography variant="h6" textAlign={sm ? 'center' : null}>
                ככה את נשארת בתודעה שלהם, והם - זוכרים אותך, מתחברים, ומתחילים לפנות.
              </Typography>
            </Stack>
          </Stack>
          <Stack direction="column" justifyContent="center" alignItems="center" gap={3}>
            <Box>
              <SCButton size="large" text="אני רוצה להתחיל לשלוח מסרים!" variant="contained"
                onClick={scrollToPurchaseSection}/>
            </Box>
          </Stack>
        </Stack>
        <Stack direction={md ? 'column' : 'row'} gap={3} justifyContent="center"
          marginTop={6} marginBottom={5} alignItems={md ? 'center' : 'start'} >
          <ImageCard image={imagekitStaticURL`rounded_hourglass.png`} text="חסכון של זמן ואנרגיה"/>
          <ImageCard image={imagekitStaticURL`rounded_paperplane.png`} text="שליחה אוטומטית של מסרים"/>
          <ImageCard image={imagekitStaticURL`rounded_email.png`} text="רשימת תפוצה חמה וקשובה"/>
          <ImageCard image={imagekitStaticURL`rounded_megaphone.png`} text="שיווק בלי מאמץ"/>
        </Stack>
      </Container>
      <Stack direction="column" justifyContent="center" gap={3}
        marginTop={2} paddingTop={4} paddingBottom={4}
        sx={{backgroundColor: theme.palette.primary.light}}>
        <Typography variant="h5" textAlign="center">
           למה כדאי שתהיה לך את האפלקצייה?
        </Typography>
        <Box margin="auto" padding={2}>
          <CardContainer>
            <Stack direction="column" gap={3} padding={4} maxWidth={600}>
              <CheckLine>
                מסרים יומיים נשלחים בשמך - בלי שתצטרכי לערבב, לפרוס או לנסח
              </CheckLine>
              <CheckLine>
                שיווק שפועל עבורך - בלי להרגיש שאת "דוחפת" או צריכה להיחשף
              </CheckLine>
              <CheckLine>
                חיסכון בזמן יקר - האפליקציה עושה את העבודה במקומך
              </CheckLine>
              <CheckLine>
                לקוחות נחשפות לשירותים שלך באופן טבעי ועדין
              </CheckLine>
              <CheckLine>
                נבנית לך רשימת תפוצה חמה של אנשים שמתחברים אלייך באמת
              </CheckLine>
              <CheckLine>
                אנשים זוכרים אותך, נמשכים אלייך - ומתחילים לפנות מיוזמתם
              </CheckLine>
            </Stack>
          </CardContainer>
        </Box>
      </Stack>
      <Banner image={imagekitStaticURL`tarot-dark-purple.png`}
        placeholderColor="#242328" backgroundPosition="top">
        <Typography variant="h5">
           מה הלקוחות שלך יקבלו בזכות האפליקציה?
        </Typography>
        <Stack direction="column" gap={2} alignItems="start" justifyContent="start">
          <CheckLine color="contrastText">
            מסר יומי אישי מהטארוט - ישר לתיבת המייל שלהם
          </CheckLine>
          <CheckLine color="contrastText">
            תחושה שיש מישהי שמכירה, רואה, ומדברת בדיוק אל הלב שלהן
          </CheckLine>
          <CheckLine color="contrastText">
            חיבור קבוע אלייך - גם בלי שיחה או פגישה
          </CheckLine>
          <CheckLine color="contrastText">
            הצעה ברורה ונגישה להעמיק איתך - בלי לחץ, בלי מכירה אגרסיבית
          </CheckLine>
          <CheckLine color="contrastText">
            השראה, משמעות וריפוי - כל יום מחדש
          </CheckLine>
          <CheckLine color="contrastText">
            תכנון הצעות פרימיום או שירותים חדשים
          </CheckLine>
          <CheckLine color="contrastText">
            חוויה קסומה שגורמת להן לרצות להמשיך איתך עוד
          </CheckLine>
        </Stack>
      </Banner>
      <Container maxWidth="md">
        <Stack gap={5} justifyContent="center"
          direction={sm ? 'column' : 'row'}
          alignItems={sm ? 'center' : null}
          paddingTop={3} paddingBottom={3}>
          <Stack direction="column" gap={4}>
            <Typography variant="h5" textAlign={sm ? 'center' : null}>
              ✨ מה האפליקציה באמת נותנת לך?
            </Typography>
            <Typography textAlign={sm ? 'center' : null}>
              אם את מרגישה שלא נעים לך "למכור",<br/>
              אם שיווק מרגיש לך מאולץ או מתיש,<br/>
              ואם את יודעת שיש לך מה לתת - אבל לא ברור איך לגרום לאנשים לפנות אלייך...
            </Typography>
            <Typography textAlign={sm ? 'center' : null}>
              האפליקציה הזו בדיוק בשבילך.
            </Typography>
            <Typography textAlign={sm ? 'center' : null}>
              במקום לשלוח מסרים אישיים לכל לקוחה, לערבב, לפרוס, לכתוב, לשלוח -<br/>
              המערכת עושה את זה במקומך, באופן אוטומטי.
            </Typography>
            <Typography textAlign={sm ? 'center' : null}>
              הלקוחות מקבלות ממך מסר יומי,<br/>
              מרגישות אותך, נזכרות בך -<br/>
              ובסוף כל מסר הן מקבלות הצעה עדינה לשירות שלך.
            </Typography>
            <Typography textAlign={sm ? 'center' : null}>
              את לא צריכה "לדחוף",<br/>
              לא להיות כל הזמן בכתיבה,<br/>
              ולא לחשוב כל פעם מחדש מה לשלוח למי.<br/>
              השיווק קורה - ואת נשארת בנוכחות, בדיוק כמו שאת אוהבת.
            </Typography>
          </Stack>
          <Box component="img" height="150px"
            src={imagekitStaticURL`spread-cards.png`} sx={{objectFit: 'contain'}}/>
        </Stack>
      </Container>
      <Container maxWidth="md" ref={purchaseSection}>
        <Stack direction="column" gap={3} alignItems="center" marginBottom={8}>
          <Typography variant="h4" textAlign="center">
            כמה זה עולה?
          </Typography>
          <Stack direction={md ? 'column' : 'row'} gap={3}>
            <SubTypeCard width={md ? null : '50%'}>
              <Box component="img" height="100px"
                src={imagekitStaticURL`moon_icon.png`} sx={{objectFit: 'contain'}}/>
              <Stack>
                <Typography textAlign="center">
                  כדי שלא תקני חתול בשק אנחנו מציעים לך
                </Typography>
                <Typography textAlign="center" variant="h5">
                  חודש ראשון ניסיון במחיר מיוחד
                </Typography>
                <Typography textAlign="center" variant="h4">
                  20 ש"ח
                </Typography>
              </Stack>
            </SubTypeCard>
            <SubTypeCard width={md ? null : '50%'}>
              <Box component="img" height="100px"
                src={imagekitStaticURL`sun_icon.png`} sx={{objectFit: 'contain'}}/>
              <Stack>
                <Typography textAlign="center">
                  אם את כבר מוכנה שהאפליקצייה תתחיל לעבוד בשבילך ותביא לך לקוחות -
                </Typography>
                <Typography textAlign="center" variant="h5">
                  מנוי שנתי
                </Typography>
                <Typography textAlign="center" variant="h4">
                  350 ש"ח בלבד
                </Typography>
                <Typography textAlign="center">
                  (פחות משקל ליום)
                </Typography>
              </Stack>
            </SubTypeCard>
          </Stack>
        </Stack>
      </Container>
      <Stack marginBottom={5}>
        <Payform/>
      </Stack>
    </Stack>;
};

export default withMainContainer(withTitle(`סימונה צ'רי - אפליקציית המסרים`, TarotmailLanding), {noHeader: true});
